import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@et/environment';
import {
  PacketListPage,
  PacketListQueryParams,
  PacketsCount,
} from '@et/typings';
import {
  Packet,
  PacketIListPagedResultApiResult,
} from '@escrowtab/packets-api-client';
import { map, Observable, retry } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PacketsApiService {
  private http = inject(HttpClient);
  private readonly baseUrl = environment.packetService.domain;

  /**
   * This method overrides files in the specified folder.
   * @param packetId - The ID of the packet to which the files belong.
   * @param folder - The folder where the files will be saved.
   * @param files - The files to be saved.
   * @returns
   */
  saveFilesToFolder(packetId: string, folder: string, files: File[]) {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file, file.name);
    });
    const params = new HttpParams({ fromObject: { packetId, folder } });
    return this.http.post(
      `${this.baseUrl}/api/packet/file/ToFolder`,
      formData,
      { params, responseType: 'text' },
    );
  }

  /**
   * Deletes a folder associated with a specific packet.
   *
   * This method sends a DELETE request to remove the specified folder from the packet.
   *
   * @param {string} packetId - The ID of the packet containing the folder to delete.
   * @param {string} folder - The name of the folder to delete.
   * @returns {Observable<string>} An observable containing the server's response as a string.
   */
  deleteFolder(packetId: string, folder: string): Observable<string> {
    const params = new HttpParams({ fromObject: { packetId, folder } });
    return this.http.delete(`${this.baseUrl}/api/packet/file/DeleteFolder`, {
      params,
      responseType: 'text',
    });
  }

  /**
   * Signs all JSON signatures for a packet using the provided file.
   *
   * This method sends a POST request with the signature file to sign all JSON signatures
   * associated with the specified packet.
   *
   * @param {string} packetId - The ID of the packet to sign.
   * @param {File} file - The signature file to use for signing.
   * @returns {Observable<any>} An observable containing the server's response.
   */
  signAllJsonSignature(
    packetId: string,
    file: File,
    eNoteSignaturesBase64: string[],
  ): Observable<void> {
    const formData = new FormData();
    formData.append('etSignatureFile', file);
    formData.append(
      'eNoteSignaturesBase64',
      JSON.stringify(eNoteSignaturesBase64),
    );
    const params = new HttpParams().set('packetId', packetId);
    return this.http.post<void>(
      `${this.baseUrl}/api/packet/file/sign/all/jsonSignature`,
      formData,
      { params },
    );
  }

  /**
   * Checks the hash of a file against the server's copy.
   * @param packetId - The ID of the packet to check the file hash for.
   * @param fileName - The name of the file to check the hash for.
   * @param hash - The hash to check.
   * @returns An observable that emits the result of the hash comparison.
   */
  checkFileHash(packetId: string, fileName: string, hash: string) {
    const params = new HttpParams()
      .set('packetId', packetId)
      .set('fileName', fileName);
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    const body = JSON.stringify(hash);
    return this.http
      .post<boolean>(`${this.baseUrl}/api/packet/file/HashComparison`, body, {
        params,
        headers,
      })
      .pipe(retry({ count: 2, delay: 100 }));
  }

  /**
   * Gets packets for a user with server-side pagination and filters.
   * GET /api/packet/info/user/pagination
   */
  getPacketsByUser(params: PacketListQueryParams): Observable<PacketListPage> {
    return this.http
      .get<PacketIListPagedResultApiResult>(
        `${this.baseUrl}/api/packet/info/user/pagination`,
        { params: this.buildPaginationParams(params) },
      )
      .pipe(map((response) => this.mapPagedPacketsResponse(response)));
  }

  /**
   * Gets packets for a company with server-side pagination and filters.
   * GET /api/packet/info/company/pagination
   */
  getPacketsByCompany(
    params: PacketListQueryParams,
  ): Observable<PacketListPage> {
    return this.http
      .get<PacketIListPagedResultApiResult>(
        `${this.baseUrl}/api/packet/info/company/pagination`,
        { params: this.buildPaginationParams(params) },
      )
      .pipe(map((response) => this.mapPagedPacketsResponse(response)));
  }

  /**
   * Maps the wrapped pagination API result into a flat page model.
   */
  mapPagedPacketsResponse(
    response: PacketIListPagedResultApiResult,
  ): PacketListPage {
    const data = response?.data;
    const packets = (data?.results ?? []) as Packet[];
    return {
      packets,
      pageNumber: data?.pageNumber ?? 1,
      pageSize: data?.pageSize ?? 0,
      totalResultCount: data?.totalResultCount ?? 0,
      hasMore: !!data?.nextPage,
      nextPage: data?.nextPage ?? null,
      previousPage: data?.previousPage ?? null,
    };
  }

  getPakcetsCount(userId: string) {
    return this.http.get<PacketsCount>(
      `${this.baseUrl}/api/packet/info/count`,
      { params: { userId } },
    );
  }

  private buildPaginationParams(params: PacketListQueryParams): HttpParams {
    const queryKeyMap: Record<keyof PacketListQueryParams, string> = {
      showArchived: 'ShowArchived',
      searchByCompany: 'SearchByCompany',
      agencyId: 'AgencyId',
      userId: 'UserId',
      closingType: 'ClosingType',
      packetState: 'PacketState',
      packetStatus: 'PacketStatus',
      packetName: 'PacketName',
      dateSignedCutOff: 'DateSignedCutOff',
      pageNumber: 'PageNumber',
      pageSize: 'PageSize',
      sortBy: 'SortBy',
      isSortAscending: 'IsSortAscending',
    };

    let httpParams = new HttpParams();
    (Object.keys(queryKeyMap) as (keyof PacketListQueryParams)[]).forEach(
      (key) => {
        const value = params[key];
        if (value === undefined || value === null || value === '') {
          return;
        }
        httpParams = httpParams.set(queryKeyMap[key], String(value));
      },
    );
    return httpParams;
  }
}
